import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Mail, Save, Send, Loader2, Copy, Eye, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/** Very forgiving `{{key}}` substitution — mirrors the backend renderer
 * so the preview panel matches what customers will actually receive. */
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
const renderPreview = (str, values) =>
  (str || '').replace(PLACEHOLDER_RE, (_, key) =>
    values[key] === undefined || values[key] === null ? '' : String(values[key])
  );

/**
 * Super Admin editor for the six automated email templates.
 * Only Super Admins can see this tab; role gating is enforced both by
 * TAB_DEFS and by the backend endpoint (verify_super_admin).
 */
export default function EmailTemplatesManager() {
  const { getAuthHeader, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [sampleValues, setSampleValues] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    if (user?.role === 'super_admin') fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/email-templates`, {
        headers: getAuthHeader(),
      });
      const list = res.data?.templates || [];
      setTemplates(list);
      setPlaceholders(res.data?.available_placeholders || []);
      setSampleValues(res.data?.sample_values || {});
      if (list.length && !activeKey) {
        setActiveKey(list[0].key);
        setSubject(list[0].subject || '');
        setBody(list[0].body || '');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const active = useMemo(
    () => templates.find((t) => t.key === activeKey),
    [templates, activeKey],
  );

  const selectTemplate = (key) => {
    if (dirty && !window.confirm('Discard unsaved changes to the current template?')) {
      return;
    }
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    setActiveKey(key);
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setDirty(false);
  };

  const onSave = async () => {
    if (!active) return;
    if (!subject.trim()) return toast.error('Subject cannot be empty');
    if (!body.trim()) return toast.error('Body cannot be empty');
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/email-templates/${active.key}`,
        { subject, body },
        { headers: getAuthHeader() },
      );
      // Replace the row in place so timestamps refresh without a full re-fetch
      setTemplates((list) =>
        list.map((t) => (t.key === active.key ? res.data : t))
      );
      setDirty(false);
      toast.success('Template saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSendTest = async () => {
    if (!active) return;
    if (!testEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testEmail)) {
      return toast.error('Enter a valid recipient email');
    }
    setTesting(true);
    try {
      await axios.post(
        `${API}/email-templates/${active.key}/test`,
        { recipient: testEmail, subject, body },
        { headers: getAuthHeader() },
      );
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const copyPlaceholder = (name) => {
    const token = `{{${name}}}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(token).catch(() => {});
    }
    toast.success(`Copied ${token}`);
  };

  const previewSubject = renderPreview(subject, sampleValues);
  const previewBody = renderPreview(body, sampleValues);

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-8 text-center" data-testid="email-templates-no-access">
        <p className="text-gray-600">Super Admin access required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-600" data-testid="email-templates-loading">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2 text-[#E87B6A]" />
        Loading templates…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="email-templates-manager">
      <header className="mb-5">
        <h2 className="text-2xl font-bold text-[#193460] flex items-center gap-2">
          <Mail className="w-7 h-7" /> Email Templates
        </h2>
        <p className="text-gray-600 text-sm mt-1">
          Edit the subject and body of the automated emails sent to
          customers and volunteers. Placeholders in <code>{`{{...}}`}</code>{' '}
          are replaced with live data at send-time; missing values collapse to blank.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[240px_1fr_260px]">
        {/* ── Template list ── */}
        <aside
          className="bg-white rounded-lg border-2 border-gray-200 p-2 self-start"
          data-testid="email-templates-list"
        >
          <ul className="space-y-1">
            {templates.map((t) => (
              <li key={t.key}>
                <button
                  type="button"
                  onClick={() => selectTemplate(t.key)}
                  data-testid={`email-template-tab-${t.key}`}
                  className={`w-full text-left px-3 py-2 rounded transition text-sm ${
                    t.key === activeKey
                      ? 'bg-[#193460] text-white font-semibold'
                      : 'text-[#193460] hover:bg-gray-100'
                  }`}
                >
                  <span className="block truncate">{t.name}</span>
                  {t.key === activeKey && dirty && (
                    <span className="block text-[10px] uppercase tracking-wider text-amber-200 mt-0.5">
                      Unsaved changes
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Editor + preview ── */}
        <section className="space-y-4">
          {active && (
            <Card className="border-2 border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#193460]">{active.name}</CardTitle>
                <p className="text-xs text-gray-500">{active.description}</p>
                {(active.updated_at || active.updated_by_email) && (
                  <p
                    className="text-[11px] text-gray-500 mt-1"
                    data-testid="email-template-meta"
                  >
                    Last updated{' '}
                    {active.updated_at
                      ? new Date(active.updated_at).toLocaleString()
                      : 'never'}
                    {active.updated_by_email ? ` by ${active.updated_by_email}` : ''}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="tpl-subject">Subject</Label>
                  <Input
                    id="tpl-subject"
                    data-testid="email-template-subject-input"
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="tpl-body">Body (basic HTML supported)</Label>
                  <textarea
                    id="tpl-body"
                    data-testid="email-template-body-input"
                    value={body}
                    onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                    rows={12}
                    className="mt-1 w-full rounded-md border border-gray-300 p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#193460]"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button
                    onClick={onSave}
                    disabled={saving || !dirty}
                    data-testid="email-template-save-btn"
                    className="bg-[#E87B6A] hover:bg-[#d66a59] text-white"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Input
                      type="email"
                      placeholder="Send test to…"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      data-testid="email-template-test-recipient"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onSendTest}
                      disabled={testing || !testEmail}
                      data-testid="email-template-send-test-btn"
                      className="border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
                    >
                      {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                      Send Test
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Preview ── */}
          <Card className="border-2 border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#193460] flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview (sample data)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="p-3 border border-gray-200 rounded bg-white text-sm"
                data-testid="email-template-preview"
              >
                <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Subject</p>
                <p className="font-semibold text-[#193460] mb-3" data-testid="email-template-preview-subject">
                  {previewSubject || <em className="text-gray-400">(empty)</em>}
                </p>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Body</p>
                <div
                  className="prose max-w-none text-sm"
                  data-testid="email-template-preview-body"
                  // Sanitised at send-time on the backend anyway; this is
                  // a Super-Admin-only view rendering their own input.
                  dangerouslySetInnerHTML={{ __html: previewBody }}
                />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ── Placeholders sidebar ── */}
        <aside
          className="bg-white rounded-lg border-2 border-gray-200 p-3 self-start"
          data-testid="email-templates-placeholders"
        >
          <h3 className="text-sm font-semibold text-[#193460] mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" /> Placeholders
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Click a token to copy it into your clipboard.
          </p>
          <ul className="space-y-1.5">
            {placeholders.map((name) => (
              <li key={name}>
                <button
                  type="button"
                  onClick={() => copyPlaceholder(name)}
                  data-testid={`email-placeholder-${name}`}
                  className="w-full inline-flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs font-mono bg-gray-50 hover:bg-[#193460]/10 text-[#193460] transition"
                  title={`Copy {{${name}}} to clipboard`}
                >
                  <span className="truncate">{`{{${name}}}`}</span>
                  <Copy className="w-3 h-3 flex-shrink-0 text-gray-400" />
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
